import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Heart, TrendingUp, Search, UserCheck, Send } from 'lucide-react';
import { Card, CardContent, Avatar, Button } from '../../components/UI';
import { useAuthStore } from '../../store/authStore';
import { usersApi } from '../../services/api';
import styles from './Team.module.css';

interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
  role: string;
  points_received: number;
  points_given: number;
  mentor_id?: string;
}

const Team: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [mentees, setMentees] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchMentees = useCallback(async () => {
    try {
      // Fetch mentees (users who have current user as mentor)
      if (user?.id) {
        const menteeResponse = await usersApi.getAll({ mentor_id: user.id });
        setMentees(menteeResponse.data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch mentees:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchMentees();
  }, [fetchMentees]);

  const filteredMembers = mentees.filter(member =>
    `${member.first_name} ${member.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const teamStats = {
    totalMentees: mentees.length,
    totalPointsReceived: mentees.reduce((sum, m) => sum + (m.points_received || 0), 0),
    totalPointsGiven: mentees.reduce((sum, m) => sum + (m.points_given || 0), 0),
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading team...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>My Mentees</h1>
          <p>View and manage your mentees</p>
        </div>
      </div>

      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <CardContent>
            <div className={styles.statIcon} style={{ background: 'rgba(12, 107, 98, 0.1)' }}>
              <UserCheck size={22} style={{ color: 'var(--primary)' }} />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statLabel}>My Mentees</span>
              <span className={styles.statValue}>{teamStats.totalMentees}</span>
            </div>
          </CardContent>
        </Card>

        <Card className={styles.statCard}>
          <CardContent>
            <div className={styles.statIcon} style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
              <Heart size={22} style={{ color: 'var(--accent)' }} />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statLabel}>Points Received</span>
              <span className={styles.statValue}>{teamStats.totalPointsReceived}</span>
            </div>
          </CardContent>
        </Card>

        <Card className={styles.statCard}>
          <CardContent>
            <div className={styles.statIcon} style={{ background: 'rgba(245, 158, 11, 0.1)' }}>
              <TrendingUp size={22} style={{ color: 'var(--warning)' }} />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statLabel}>Points Given</span>
              <span className={styles.statValue}>{teamStats.totalPointsGiven}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent>
          <div className={styles.tableHeader}>
            <h3>My Mentees</h3>
            <div className={styles.searchBox}>
              <Search size={18} />
              <input
                type="text"
                placeholder="Search mentees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {filteredMembers.length === 0 ? (
            <div className={styles.empty}>
              <Users size={48} />
              <h3>No mentees assigned</h3>
              <p>You don't have any mentees assigned to you yet</p>
            </div>
          ) : (
            <div className={styles.membersList}>
              <div className={styles.listHeader}>
                <span>Member</span>
                <span>Points Received</span>
                <span>Actions</span>
              </div>
              {filteredMembers.map((member) => (
                <div key={member.id} className={styles.memberItem}>
                  <div className={styles.memberInfo}>
                    <Avatar
                      src={member.avatar_url}
                      name={`${member.first_name} ${member.last_name}`}
                      size="md"
                    />
                    <div className={styles.memberDetails}>
                      <span className={styles.memberName}>
                        {member.first_name} {member.last_name}
                      </span>
                      <span className={styles.memberEmail}>{member.email}</span>
                    </div>
                  </div>
                  <span className={styles.memberPoints}>{member.points_received || 0}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/appreciate')}
                  >
                    <Send size={14} /> Send Ripple
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Team;
